// Offboarding domain logic — pure, dependency-free so both client and server
// can compute notice periods the same way. See OffboardingType in enum.ts.
import { OffboardingDocType, OffboardingResponsible, OffboardingType } from './enum.js';

export const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Statutory minimum job-search leave per day (Turkish Labour Law art. 27): 2 hours.
 * HR may grant more, never less. Used as a clamp floor on both client and server.
 */
export const JOB_SEARCH_MIN_DAILY_HOURS = 2;

/** Default working days per week (Mon–Fri). HR may raise it to include Saturday etc. */
export const DEFAULT_WORK_DAYS_PER_WEEK = 5;

/** Standard full working day in hours — used to convert accrued job-search hours to days. */
export const STANDARD_WORK_DAY_HOURS = 8;

/**
 * Counts working days in the inclusive range [start, end], treating the FIRST
 * `workDaysPerWeek` days of the week as working days:
 *   5 → Mon–Fri, 6 → Mon–Sat, 7 → every day.
 * The count naturally depends on which weekday the range starts on (art. 27: the
 * leave runs from the resignation/notice start date over the notice window).
 */
export const workingDaysBetween = (start: Date, end: Date, workDaysPerWeek: number): number => {
  const from = stripTime(start);
  const to = stripTime(end);
  if (to.getTime() < from.getTime()) return 0;
  const clampedPerWeek = Math.max(0, Math.min(7, Math.round(workDaysPerWeek)));
  if (clampedPerWeek === 0) return 0;

  let count = 0;
  const cursor = new Date(from);
  while (cursor.getTime() <= to.getTime()) {
    // getDay(): 0=Sun..6=Sat. Convert to Monday-based index 0=Mon..6=Sun so the
    // first `clampedPerWeek` weekdays (Mon…) count as working days.
    const mondayBased = (cursor.getDay() + 6) % 7;
    if (mondayBased < clampedPerWeek) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

/**
 * Total job-search leave hours over the notice window = working days in
 * [processStart, plannedExit] × daily hours. Working days depend on how many days
 * a week the employee works (default 5). Informational/policy figure only. Returns
 * 0 for a disabled/empty setup so nothing is shown.
 */
export const jobSearchLeaveTotalHours = (
  processStart: Date,
  plannedExit: Date,
  dailyHours: number,
  workDaysPerWeek: number = DEFAULT_WORK_DAYS_PER_WEEK
): number => {
  if (dailyHours <= 0) return 0;
  const workingDays = workingDaysBetween(processStart, plannedExit, workDaysPerWeek);
  return workingDays * dailyHours;
};

/**
 * Converts accrued job-search leave hours into full working days at the standard
 * 8-hour day (e.g. 28h → 3.5 days). Informational; may be fractional. Returns 0 for
 * non-positive input. Rounded to one decimal to avoid float noise (3.499999…).
 */
export const jobSearchLeaveBlockDays = (totalHours: number): number => {
  if (totalHours <= 0) return 0;
  return Math.round((totalHours / STANDARD_WORK_DAY_HOURS) * 10) / 10;
};

/**
 * When the leave is taken in one block at the END of the notice (END_OF_NOTICE mode),
 * this returns the working-day span the block covers: counting back from the planned
 * exit date over working days until the accrued days are used up.
 *
 * The block ends on the last working day on/before the exit date and starts on the
 * working day that consumes the final (possibly partial) day of leave. A partial day
 * still occupies a whole calendar working day, so we ceil the day count for the span.
 *
 * Returns null when there's nothing to show (no hours, or invalid dates).
 */
export const jobSearchLeaveBlockRange = (
  plannedExit: Date,
  totalHours: number,
  workDaysPerWeek: number = DEFAULT_WORK_DAYS_PER_WEEK
): { start: Date; end: Date; days: number } | null => {
  const days = jobSearchLeaveBlockDays(totalHours);
  if (days <= 0) return null;
  const clampedPerWeek = Math.max(1, Math.min(7, Math.round(workDaysPerWeek)));

  const isWorkingDay = (d: Date) => ((d.getDay() + 6) % 7) < clampedPerWeek;

  // A partial day still occupies a whole working day, so ceil for how many working
  // days the block physically spans.
  const spanDays = Math.ceil(days);

  // End = last working day on/before the exit date.
  const end = stripTime(plannedExit);
  while (!isWorkingDay(end)) end.setDate(end.getDate() - 1);

  // Walk back `spanDays` working days (inclusive of `end`) to find the start.
  const start = new Date(end);
  let counted = 1;
  while (counted < spanDays) {
    start.setDate(start.getDate() - 1);
    if (isWorkingDay(start)) counted += 1;
  }
  return { start, end, days };
};

/** One required document in the offboarding checklist, with its workflow shape. */
export type OffboardingDocRequirement = {
  docType: OffboardingDocType;
  responsible: OffboardingResponsible;
  requiresEmployeeApproval: boolean;
  requiresHrApproval: boolean;
  /**
   * Two-stage signing: HR uploads the document, the employee must re-upload a signed
   * copy, then HR approves. The employee's signed re-upload stands in for their
   * approval, so requiresEmployeeApproval stays false for these.
   */
  requiresEmployeeSignature?: boolean;
};

// The approved document matrix (who uploads, whose approvals are needed).
// RELEASE_WAIVER, LEAVE_ENTITLEMENT, PAYROLL and EXIT_CERTIFICATE follow the
// sign-and-return flow: HR uploads → employee signs & re-uploads → HR approves.
// RESIGNATION_LETTER and TERMINATION_NOTICE are exit-type specific (Rule 15);
// the rest are always required.
const COMMON_OFFBOARDING_DOCS: OffboardingDocRequirement[] = [
  { docType: OffboardingDocType.LEAVE_ENTITLEMENT, responsible: OffboardingResponsible.HR, requiresEmployeeApproval: false, requiresHrApproval: true, requiresEmployeeSignature: true },
  { docType: OffboardingDocType.INVENTORY_RETURN, responsible: OffboardingResponsible.EMPLOYEE, requiresEmployeeApproval: false, requiresHrApproval: true },
  { docType: OffboardingDocType.RELEASE_WAIVER, responsible: OffboardingResponsible.HR, requiresEmployeeApproval: false, requiresHrApproval: true, requiresEmployeeSignature: true },
  { docType: OffboardingDocType.EXIT_CERTIFICATE, responsible: OffboardingResponsible.HR, requiresEmployeeApproval: false, requiresHrApproval: true, requiresEmployeeSignature: true },
  { docType: OffboardingDocType.PAYROLL, responsible: OffboardingResponsible.HR, requiresEmployeeApproval: false, requiresHrApproval: true, requiresEmployeeSignature: true },
];

/**
 * Required documents for an offboarding process, chosen by exit type (Rule 15):
 *  - RESIGNATION            → employee uploads the resignation letter (HR approves)
 *  - TERMINATION_BY_EMPLOYER → HR uploads the termination notice (employee + HR approve)
 */
// Accept the string form too, so callers holding the server's Prisma-generated
// OffboardingType enum (same string values, different nominal type) can pass it
// without casting.
export const getRequiredOffboardingDocuments = (
  exitType: OffboardingType | `${OffboardingType}`
): OffboardingDocRequirement[] => {
  const exitSpecific: OffboardingDocRequirement =
    exitType === OffboardingType.RESIGNATION
      ? {
          docType: OffboardingDocType.RESIGNATION_LETTER,
          responsible: OffboardingResponsible.EMPLOYEE,
          requiresEmployeeApproval: false,
          requiresHrApproval: true,
        }
      : {
          docType: OffboardingDocType.TERMINATION_NOTICE,
          responsible: OffboardingResponsible.HR,
          requiresEmployeeApproval: true,
          requiresHrApproval: true,
        };
  return [exitSpecific, ...COMMON_OFFBOARDING_DOCS];
};

/**
 * Statutory notice tiers (Turkish Labour Law art. 17), expressed in weeks by
 * tenure. The notice period is a function of how long the employee has worked:
 *  - < 6 months        → 2 weeks
 *  - 6 months – 1.5 yr → 4 weeks
 *  - 1.5 yr – 3 yr     → 6 weeks
 *  - > 3 yr            → 8 weeks
 * The boundaries are inclusive on the lower side the way the law reads them
 * (e.g. exactly 6 months already earns the 4-week tier).
 */
export const NOTICE_TIERS = [
  { minMonths: 0, maxMonths: 6, weeks: 2 },
  { minMonths: 6, maxMonths: 18, weeks: 4 },
  { minMonths: 18, maxMonths: 36, weeks: 6 },
  { minMonths: 36, maxMonths: Infinity, weeks: 8 },
] as const;

const DAYS_PER_WEEK = 7;

/** Whole months of tenure between two dates (calendar months, not 30-day blocks). */
export const tenureInMonths = (hireDate: Date, asOf: Date): number => {
  let months = (asOf.getFullYear() - hireDate.getFullYear()) * 12 + (asOf.getMonth() - hireDate.getMonth());
  // If we haven't reached the day-of-month yet, the current month isn't complete.
  if (asOf.getDate() < hireDate.getDate()) {
    months -= 1;
  }
  return Math.max(0, months);
};

/** Notice period in weeks for a given tenure (art. 17 tiers). */
export const noticeWeeksForTenure = (months: number): number => {
  const tier = NOTICE_TIERS.find((t) => months >= t.minMonths && months < t.maxMonths);
  // Infinity upper bound guarantees a match; fall back to the top tier defensively.
  return tier ? tier.weeks : NOTICE_TIERS[NOTICE_TIERS.length - 1].weeks;
};

export type NoticePeriodResult = {
  tenureMonths: number;
  noticeWeeks: number;
  noticeDays: number;
  /** Earliest legally allowed exit date = processStart + full notice. */
  minPlannedExitDate: Date;
};

/**
 * Computes the statutory notice period for an employee.
 * @param hireDate       When the employment (this period) started.
 * @param processStart   When the offboarding process is initiated (notice clock start).
 */
export const calculateNoticePeriod = (hireDate: Date, processStart: Date): NoticePeriodResult => {
  const tenureMonths = tenureInMonths(hireDate, processStart);
  const noticeWeeks = noticeWeeksForTenure(tenureMonths);
  const noticeDays = noticeWeeks * DAYS_PER_WEEK;
  const minPlannedExitDate = new Date(processStart.getTime() + noticeDays * MS_PER_DAY);
  return { tenureMonths, noticeWeeks, noticeDays, minPlannedExitDate };
};

/** Inclusive day count between two dates (used for "days used" of the notice). */
export const daysBetween = (start: Date, end: Date): number =>
  Math.round((stripTime(end).getTime() - stripTime(start).getTime()) / MS_PER_DAY);

const stripTime = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export type NoticeValidationInput = {
  hireDate: Date;
  processStart: Date;
  plannedExitDate: Date;
};

export type NoticeValidationResult = {
  valid: boolean;
  /** True when plannedExitDate is outside [processStart, processStart + notice]. */
  outOfRange: boolean;
  noticeDays: number;
  /** The notice cap: the latest allowed exit = processStart + full notice. */
  maxPlannedExitDate: Date;
  /** Notice days actually used (processStart → plannedExit), clamped to [0, notice]. */
  noticeDaysUsed: number;
  /** Notice days waived (full notice − used), clamped at 0. */
  noticeDaysUnused: number;
};

/**
 * Validates a chosen exit date: it must fall within [processStart, processStart +
 * full notice]. The notice period is the MAXIMUM runway — the employee may leave
 * any day up to it, but not before today nor after the cap.
 */
export const validatePlannedExit = (input: NoticeValidationInput): NoticeValidationResult => {
  const { hireDate, processStart, plannedExitDate } = input;
  const { noticeDays, minPlannedExitDate } = calculateNoticePeriod(hireDate, processStart);
  // calculateNoticePeriod returns processStart + notice, which here is the cap.
  const maxPlannedExitDate = minPlannedExitDate;

  const exit = stripTime(plannedExitDate).getTime();
  const outOfRange = exit < stripTime(processStart).getTime() || exit > stripTime(maxPlannedExitDate).getTime();

  const usedRaw = daysBetween(processStart, plannedExitDate);
  const noticeDaysUsed = Math.max(0, Math.min(usedRaw, noticeDays));
  const noticeDaysUnused = Math.max(0, noticeDays - noticeDaysUsed);

  return {
    valid: !outOfRange,
    outOfRange,
    noticeDays,
    maxPlannedExitDate,
    noticeDaysUsed,
    noticeDaysUnused,
  };
};
